/* data/index.js — deep-clone accessors over the extracted prototype seeds.
 *
 * The prototype MUTATES its seed arrays in place (splice/push on module vars),
 * so every consumer here gets a fresh deep clone — the JSON modules are never
 * handed out by reference. Counts are sanity-checked once at module load
 * (console.warn, never throw: a count drift must not brick the workspace). */
import CATALOG_SEED from "./catalog.json";
import SEEDS from "./seeds.json";

const clone = (v) => JSON.parse(JSON.stringify(v));

/* ── seed count sanity asserts (extraction contract, cat-data.md §5) ────── */
function warn(name, got, want) {
  if (got !== want) console.warn(`[catalog seeds] ${name}: expected ${want}, got ${got}`);
}
(function assertCounts() {
  try {
    warn("CATALOG", CATALOG_SEED.length, 206);
    warn("PRICE_GROUPS", SEEDS.PRICE_GROUPS.length, 9);
    warn("HANDLES", SEEDS.HANDLES.length, 8);
    warn("GOLA", SEEDS.GOLA.length, 3);
    warn("CARCASS_COLORS", SEEDS.CARCASS_COLORS.length, 3);
    warn("PLINTH", SEEDS.PLINTH.length, 4);
    warn("PLINTH_MATERIALS", SEEDS.PLINTH_MATERIALS.length, 3);
    warn("APPLIANCES", SEEDS.APPLIANCES.length, 11);
    warn("DOOR_STYLES", SEEDS.DOOR_STYLES.length, 4);
    warn("COUNTERTOPS", SEEDS.COUNTERTOPS.length, 6);
    warn("WORKTOPS", SEEDS.WORKTOPS.length, 5);
    warn("BACKSPLASH", SEEDS.BACKSPLASH.length, 4);
    warn("PG_CATS", SEEDS.PG_CATS.length, 5);
    warn("FABRICS.indoor", SEEDS.FABRICS.indoor.length, 6);
    warn("FABRICS.leather", SEEDS.FABRICS.leather.length, 2);
    warn("FABRICS.outdoor", SEEDS.FABRICS.outdoor.length, 3);
    warn("FABRICS.rope", (SEEDS.FABRICS.rope || []).length, 3);
    warn("EQUIPMENT categories", Object.keys(SEEDS.EQUIPMENT).length, 15);
    warn("PLINTH_COLORS", SEEDS.PLINTH_COLORS.length, 5);
    warn("DOOR_SYSTEMS", SEEDS.DOOR_SYSTEMS.length, 2);
  } catch (e) {
    console.warn("[catalog seeds] count assert failed:", e);
  }
})();

/* ── blob-shaped seed snapshot (field names = mirror/backend blob fields) ── */
export function freshSeeds() {
  return {
    groups: clone(SEEDS.PRICE_GROUPS),
    handles: clone(SEEDS.HANDLES),
    gola: clone(SEEDS.GOLA),
    carcass: clone(CATALOG_SEED),
    appliances: clone(SEEDS.APPLIANCES),
    colors: clone(SEEDS.CARCASS_COLORS),
    plinth: clone(SEEDS.PLINTH),
    pmat: clone(SEEDS.PLINTH_MATERIALS),
    dstyles: clone(SEEDS.DOOR_STYLES),
    countertops: clone(SEEDS.COUNTERTOPS),
    lighting: clone(SEEDS.LIGHTING),
    fabrics: clone(SEEDS.FABRICS),
    worktops: clone(SEEDS.WORKTOPS),
    backsplash: clone(SEEDS.BACKSPLASH),
    equipment: clone(SEEDS.EQUIPMENT),
    pgCats: clone(SEEDS.PG_CATS),
  };
}

/* ── individual accessors (each call = fresh clone) ─────────────────────── */
export const seedCatalog = () => clone(CATALOG_SEED);
export const seedPriceGroups = () => clone(SEEDS.PRICE_GROUPS);
export const seedHandles = () => clone(SEEDS.HANDLES);
export const seedGola = () => clone(SEEDS.GOLA);
export const seedCarcassColors = () => clone(SEEDS.CARCASS_COLORS);
export const seedPlinth = () => clone(SEEDS.PLINTH);
export const seedPlinthMaterials = () => clone(SEEDS.PLINTH_MATERIALS);
export const seedAppliances = () => clone(SEEDS.APPLIANCES);
export const seedDoorStyles = () => clone(SEEDS.DOOR_STYLES);
export const seedCountertops = () => clone(SEEDS.COUNTERTOPS);
export const seedLighting = () => clone(SEEDS.LIGHTING);
export const seedFabrics = () => clone(SEEDS.FABRICS);
export const seedWorktops = () => clone(SEEDS.WORKTOPS);
export const seedBacksplash = () => clone(SEEDS.BACKSPLASH);
export const seedEquipment = () => clone(SEEDS.EQUIPMENT);
export const seedPgCats = () => clone(SEEDS.PG_CATS);
export const seedDoorSystems = () => clone(SEEDS.DOOR_SYSTEMS);
export const seedPlinthColors = () => clone(SEEDS.PLINTH_COLORS);

/* ── static classification constants (prototype 54686-54692 — never
 *    persisted or mutated; code constants, not seed data) ─────────────────── */
export const TAXONOMY = {
  kitchens: { label: "Kitchens", subs: ["Horizon", "Open", "Aria", "Mont", "Concept One"], types: ["Base Unit", "Drawer Unit", "Corner Base", "Sink Unit", "Tall Unit", "Column", "Wall Unit", "Appliance Unit", "Island", "End & Filler Panels", "Open Unit"] },
  systems: { label: "Systems", subs: ["Wardrobes", "Walk-In Closets", "Beds", "Night Complements", "Day Systems", "Side Boards"] },
  indoor: { label: "Indoor", subs: ["Sofas", "Armchairs", "Chaise Lounge", "Coffee Tables", "Side Tables", "Dining Tables", "Dining Chairs"] },
  outdoor: { label: "Outdoor", subs: ["Sofas", "Armchairs", "Chaise Lounge", "Coffee Tables", "Side Tables", "Dining Tables", "Dining Chairs"] },
};
export const TOP_CATS = [["kitchens", "Kitchens"], ["systems", "Systems"], ["indoor", "Indoor"], ["outdoor", "Outdoor"]];
/* Top-bar product-family tab → osLine mapping (prototype pnav, 56214). */
export const FAM = { kitchen: "kitchens", wardrobe: "systems", sofa: "indoor", outdoor: "outdoor" };
/* Carcass material choices for the (legacy) material select (prototype 54698). */
export const CARCASS_MATERIALS = ["18mm E1 Melamine particleboard", "18mm MR Melamine particleboard", "16mm Melamine particleboard", "18mm MDF", "18mm Birch plywood", "19mm Blockboard"];

/* ── MATERIAL LIBRARY — single source of truth (v4.56, HTML 57792-57802).
 *    Each Material Library page + the option columns it owns. The rail's
 *    "Material Library" group AND the Products "Components & allowed options"
 *    columns both derive from this list. ─────────────────────────────────── */
export const MATLIB = [
  { page: "finishes", label: "Carcasses", cols: [["carcass", "Carcasses"], ["shelves", "Internal Shelves"], ["drawers", "Drawers"], ["organizers", "Drawer Organizers"], ["bins", "Bins"], ["corners", "Corners"], ["pullouts", "Pull-outs"], ["columns", "Columns"]] },
  { page: "accessories", label: "Fronts", cols: [["groups", "Fronts"]] },
  { page: "handles", label: "Handles", cols: [["handles", "Handles"]] },
  // 2026-08-09: Gola got its OWN rail page (it used to be appended to Handles)
  { page: "gola", label: "Gola", cols: [["gola", "Gola"]] },
  { page: "plinth", label: "Plinth", cols: [["plinth", "Plinth"]] },
  { page: "lighting", label: "Lighting", cols: [["lighting", "Lighting"]] },
  { page: "worktops", label: "Worktops", cols: [] },
];
export function matlibCols() {
  const out = [];
  MATLIB.forEach((m) => { (m.cols || []).forEach((c) => { out.push(c); }); });
  out.push(["equipment", "Equipment"]);
  return out;
}

/* v4.56 equipment categories (7 appliance + 8 internal-fitting). */
export const EQ_CATS = ["sinks", "taps", "hobs", "ovens", "hoods", "fridges", "dishwashers", "bins", "corners", "pullouts", "columns", "organizers", "hinges", "drawers", "shelves"];
